import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../Styles/Books.css';

// UI 字串：英文為主，中文為備選翻譯（透過右上角切換）
const I18N = {
  en: {
    eyebrow: 'A Quiet Shelf',
    title: 'My Bookshelf',
    subtitle: "Pages I've turned, and pages still turning.",
    tabInProgress: 'In Progress',
    tabCompleted: 'Completed',
    emptyTitle: 'No new pages yet',
    emptySub: '— a page waiting to be turned —',
    back: 'Home',
    langLabel: 'Language',
    by: 'by',
    pages: 'pages',
    firstPublished: 'First published',
  },
  zh: {
    eyebrow: '靜靜的書架',
    title: '我的書架',
    subtitle: '已翻過的頁，與正在翻開的頁。',
    tabInProgress: '進行中',
    tabCompleted: '已完成',
    emptyTitle: '尚未翻開新章節',
    emptySub: '—— 等待翻開的頁 ——',
    back: '回首頁',
    langLabel: '語言',
    by: '作者',
    pages: '頁',
    firstPublished: '初版',
  },
};

// 書本資料：每本書包含基本書目資訊 + 中英簡述
const booksData = {
  inProgress: [
    {
      id: 'adam-smith-american-enterprise',
      title: 'Adam Smith and the Origins of American Enterprise',
      author: 'Roy C. Smith',
      year: 2002,
      pages: 224,
      genre: { en: 'Economics · History', zh: '經濟 · 歷史' },
      cover: '/images/my%20upset%20tracker%20asset/adam%20smith.jpeg',
      description: {
        en: 'How the Founding Fathers turned to a great economist\u2019s writings and shaped the early American economy.',
        zh: '建國先賢如何汲取亞當·斯密的思想，奠定早期美國經濟的根基。',
      },
    },
    {
      id: 'artificial-intelligence-10-things',
      title: 'Artificial Intelligence: 10 Things You Should Know',
      author: 'Tim Rocktäschel',
      year: 2024,
      pages: 120,
      genre: { en: 'Technology · Society', zh: '科技 · 社會' },
      cover: '/images/my%20upset%20tracker%20asset/AI.png',
      description: {
        en: 'A concise primer on ten ideas that quietly power the AI shaping our everyday lives.',
        zh: '以十個關鍵概念，淺白地解構 AI 如何悄悄滲入日常生活。',
      },
    },
  ],
  completed: [
    {
      id: 'game-theory-101',
      title: 'Game Theory 101: The Complete Textbook',
      author: 'William Spaniel',
      year: 2011,
      pages: 270,
      genre: { en: 'Game Theory · Mathematics', zh: '博弈論 · 數學' },
      cover: '/images/game%20theory.jpeg',
      description: {
        en: 'A user-friendly introduction to game theory, drawn from the popular YouTube series.',
        zh: '取材自熱門 YouTube 系列的博弈論入門讀本，循序漸進、清晰易懂。',
      },
    },
  ],
};

const TABS = [
  { key: 'inProgress', labelKey: 'tabInProgress' },
  { key: 'completed', labelKey: 'tabCompleted' },
];

export default function Books() {
  const [activeTab, setActiveTab] = useState('inProgress');
  const [lang, setLang] = useState('en');
  const t = I18N[lang];

  const books =
    activeTab === 'inProgress' ? booksData.inProgress : booksData.completed;

  return (
    <div
      className={`books-page books-lang-${lang}`}
      lang={lang === 'zh' ? 'zh-Hant' : 'en'}
    >
      <div className="books-paper" />

      <div className="books-container">
        <Link to="/" className="books-back-link" aria-label="Back to home">
          <span className="books-back-arrow" aria-hidden="true">
            ←
          </span>
          <span className="books-back-text">{t.back}</span>
        </Link>

        <div
          className="books-lang-toggle"
          role="group"
          aria-label={t.langLabel}
        >
          <button
            type="button"
            className={`books-lang ${lang === 'en' ? 'active' : ''}`}
            onClick={() => setLang('en')}
            aria-pressed={lang === 'en'}
          >
            EN
          </button>
          <span className="books-lang-sep" aria-hidden="true">
            ·
          </span>
          <button
            type="button"
            className={`books-lang ${lang === 'zh' ? 'active' : ''}`}
            onClick={() => setLang('zh')}
            aria-pressed={lang === 'zh'}
          >
            中文
          </button>
        </div>

        <header className="books-header">
          <h1 className="books-title">{t.title}</h1>

          <span className="books-divider" aria-hidden="true" />
        </header>

        <nav className="books-tabs" aria-label="Book status filter">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`books-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
              aria-pressed={activeTab === tab.key}
            >
              {t[tab.labelKey]}
            </button>
          ))}
        </nav>

        <main className="books-shelf" key={`${activeTab}-${lang}`}>
          {books.length === 0 ? (
            <div className="books-empty">
              <p className="books-empty-line">{t.emptyTitle}</p>
              <p className="books-empty-sub">{t.emptySub}</p>
            </div>
          ) : (
            <div className="books-grid">
              {books.map((book) => {
                const genre =
                  typeof book.genre === 'object'
                    ? book.genre[lang]
                    : book.genre;
                const description =
                  typeof book.description === 'object'
                    ? book.description[lang]
                    : book.description;

                return (
                  <article key={book.id} className="book-card">
                    <div className="book-cover-wrap">
                      <img
                        className="book-cover"
                        src={book.cover}
                        alt={book.title}
                        loading="lazy"
                      />
                    </div>

                    <div className="book-info">
                      <h2 className="book-title">{book.title}</h2>
                      {book.author && (
                        <p className="book-author">
                          <span className="book-author-prefix">{t.by}</span>{' '}
                          <span className="book-author-name">
                            {book.author}
                          </span>
                        </p>
                      )}

                      <ul className="book-meta">
                        {book.year && (
                          <li className="book-meta-item">
                            <span className="book-meta-label">
                              {t.firstPublished}
                            </span>
                            <span className="book-meta-value">{book.year}</span>
                          </li>
                        )}
                        {book.pages && (
                          <li className="book-meta-item">
                            <span className="book-meta-value">
                              {book.pages}
                            </span>
                            <span className="book-meta-label">{t.pages}</span>
                          </li>
                        )}
                        {genre && (
                          <li className="book-meta-item book-meta-genre">
                            <span className="book-meta-value">{genre}</span>
                          </li>
                        )}
                      </ul>

                      {description && (
                        <p className="book-description">{description}</p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </main>

        <footer className="books-footer" aria-hidden="true">
          <span className="books-footer-mark">· · ·</span>
        </footer>
      </div>
    </div>
  );
}
